import React, { createContext, useEffect, useMemo } from 'react';

type ConsentLevels = Array<'C0001' | 'C0002' | 'C0003' | 'C0004'>;

type FriendlyConsentLevels = {
  /**
   * C0001: Strictly necessary cookies
   *
   * Enabled by default. Cookies that are essential for the website to function properly.
   */
  necessary: boolean;
  /**
   * C0002: Performance cookies
   *
   * Cookies that help us understand how visitors interact with the website by collecting and reporting information anonymously.
   */
  performance: boolean;
  /**
   * C0003: Functional cookies
   *
   * Cookies that enhance the website's functionality and personalization.
   */
  functional: boolean;
  /**
   * C0004: Targeting cookies
   *
   * Cookies that are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.
   */
  targeting: boolean;
};

interface OneTrustContextType {
  consentLevels: ConsentLevels | null;
  friendlyConsentLevels?: FriendlyConsentLevels;
}

export const OneTrustContext = createContext<OneTrustContextType>({
  consentLevels: null,
});

type OneTrustProviderProps = {
  children: React.ReactNode;
};

const OneTrustProvider: React.FC<OneTrustProviderProps> = ({ children }) => {
  const [consentLevels, setConsentLevels] = React.useState<Array<
    'C0001' | 'C0002' | 'C0003' | 'C0004'
  > | null>(null);

  useEffect(() => {
    function getConsentLevels(): ConsentLevels | null {
      if (typeof window === 'undefined') return null;
      const levels = window.OnetrustActiveGroups;

      if (!levels) return null;

      const parsedLevels = levels
        .split(',')
        .filter((value) => value) as ConsentLevels;
      return parsedLevels;
    }
    setConsentLevels(getConsentLevels());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleOneTrustGroupsUpdated: EventListener = (e) => {
      const event = e as CustomEvent<ConsentLevels>;
      if (event.detail) {
        setConsentLevels(event.detail);
      }
    };

    window.addEventListener(
      'OneTrustGroupsUpdated',
      handleOneTrustGroupsUpdated,
    );
    return () => {
      window.removeEventListener(
        'OneTrustGroupsUpdated',
        handleOneTrustGroupsUpdated,
      );
    };
  }, []);

  const friendlyConsentLevels: FriendlyConsentLevels = useMemo(
    () => ({
      necessary: consentLevels?.includes('C0001') ?? false,
      performance: consentLevels?.includes('C0002') ?? false,
      functional: consentLevels?.includes('C0003') ?? false,
      targeting: consentLevels?.includes('C0004') ?? false,
    }),
    [consentLevels],
  );

  const contextValue = useMemo(
    () => ({
      consentLevels,
      friendlyConsentLevels,
    }),
    [consentLevels, friendlyConsentLevels],
  );

  return (
    <OneTrustContext.Provider value={contextValue}>
      {children}
    </OneTrustContext.Provider>
  );
};

export default OneTrustProvider;

export function useOneTrustContext() {
  const context = React.useContext(OneTrustContext);
  if (context === undefined) {
    throw new Error(
      'useOneTrustContext must be used within a OneTrustProvider',
    );
  }
  return context;
}
